import type {
  GrantProgram,
  GoNoGo,
  ProgramScore,
} from '@/types/roadmap.types';

const WEIGHTS = {
  mission_alignment: 0.25,
  eligibility_gate: 0.25,
  readiness: 0.20,
  funding_fit: 0.15,
  strategic_value: 0.10,
  application_burden: 0.05,
} as const;

const GO_NO_GO_RANK: Record<GoNoGo, number> = {
  primary: 0,
  go: 1,
  conditional: 2,
  monitor: 3,
  no_go: 4,
};

function computeComposite(score: Omit<ProgramScore, 'composite'>): number {
  const raw =
    score.mission_alignment * WEIGHTS.mission_alignment +
    score.eligibility_gate * WEIGHTS.eligibility_gate +
    score.readiness * WEIGHTS.readiness +
    score.funding_fit * WEIGHTS.funding_fit +
    score.strategic_value * WEIGHTS.strategic_value +
    score.application_burden * WEIGHTS.application_burden;
  return Math.round(raw * 10) / 10;
}

export function scoreAllPrograms(programs: GrantProgram[]): GrantProgram[] {
  return programs.map((p) => {
    if (!p.score) return p;

    const composite = computeComposite(p.score);
    return {
      ...p,
      score: { ...p.score, composite },
    };
  });
}

/**
 * Canonical priority sort: go → conditional → monitor → no_go,
 * then descending by composite score within each tier.
 */
export function sortByPriority(programs: GrantProgram[]): GrantProgram[] {
  return [...programs].sort((a, b) => {
    const tierDiff = GO_NO_GO_RANK[a.go_no_go] - GO_NO_GO_RANK[b.go_no_go];
    if (tierDiff !== 0) return tierDiff;
    return (b.score?.composite ?? 0) - (a.score?.composite ?? 0);
  });
}
